import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useHousehold } from '@/context/HouseholdContext'
import type { Category, CategoryKind } from '@/types/db'

// Por padrão traz só as ATIVAS (arquivadas somem dos seletores).
// Configurações → Categorias usa includeArchived = true.
export function useCategories(includeArchived = false) {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['categories', householdId, includeArchived],
    queryFn: async () => {
      let q = supabase.from('categories').select('*').eq('household_id', householdId).order('name')
      if (!includeArchived) q = q.eq('archived', false)
      const { data, error } = await q
      if (error) throw error
      return data as Category[]
    },
  })
}

// Agrupa categorias em pais + filhos para selects hierárquicos.
export interface CategoryTree {
  parent: Category
  children: Category[]
}

export function buildTree(categories: Category[], kind?: Category['kind']): CategoryTree[] {
  const filtered = kind ? categories.filter((c) => c.kind === kind) : categories
  const parents = filtered.filter((c) => c.parent_id === null)
  return parents
    .map((parent) => ({
      parent,
      children: filtered.filter((c) => c.parent_id === parent.id),
    }))
    .sort((a, b) => a.parent.name.localeCompare(b.parent.name))
}

// Categorias que um LANÇAMENTO de dado tipo (income/expense) pode escolher: as do
// próprio tipo MAIS as de INVESTIMENTO (day-trade tem ganho=income e perda=expense,
// os dois precisam poder cair na mesma categoria de investimento).
export function buildPicker(categories: Category[], txKind: 'income' | 'expense'): CategoryTree[] {
  return [...buildTree(categories, txKind), ...buildTree(categories, 'investment')]
}

export interface CategoryInput {
  name: string
  icon: string | null
  kind: CategoryKind
  parent_id: string | null
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['categories'] })
  void qc.invalidateQueries({ queryKey: ['transactions'] })
  void qc.invalidateQueries({ queryKey: ['budgets'] })
}

export function useSaveCategory() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: CategoryInput }) => {
      if (id) {
        const { error } = await supabase.from('categories').update(values).eq('id', id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('categories').insert({ ...values, household_id: householdId })
        if (error) throw error
      }
    },
    onSuccess: () => invalidateAll(qc),
  })
}

// Habilitar/desabilitar (arquivar) — preserva histórico e orçamentos.
export function useSetCategoryArchived() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase.from('categories').update({ archived }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateAll(qc),
  })
}

// Excluir: transações ficam SEM categoria (FK on delete set null); os orçamentos
// da categoria e as SUBCATEGORIAS são apagados (cascade).
export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('categories').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateAll(qc),
  })
}

// Quantas transações usam a categoria (aviso antes de excluir).
export async function fetchCategoryTxCount(id: string): Promise<number> {
  const { count, error } = await supabase
    .from('transactions')
    .select('id', { count: 'exact', head: true })
    .eq('category_id', id)
  if (error) throw error
  return count ?? 0
}
